import type { FC } from 'react';
import {
  Bot,
  MessageSquareMore,
  BotMessageSquare,
  MessageCircleMore,
} from 'lucide-react';
import { ChatbotIcon } from '../../types/customTypes';
import { useChatbotConfig } from '../../hooks/useChatbotConfig';
import { isWhiteColor } from '../../utils/colorUtils';

interface ChatbotAvatarProps {
  /** Custom className for additional styling */
  className?: string;
  /** Optional override for icon type (1-4 for preset icons, 99 for custom) */
  icon?: number;
  /** Optional override for custom icon URL */
  iconUrl?: string | null;
  /** Optional override for theme color (hex string) */
  color?: string;
  /** Optional override for color over theme (avatar background hex string) */
  colorOverTheme?: string;
}

/**
 * ChatbotAvatar - Reusable chatbot avatar component
 *
 * Displays the appropriate chatbot icon based on configuration,
 * with consistent styling across all thread components.
 * Used in ThreadWelcome, AssistantMessage, and TypingIndicator.
 *
 * Can accept optional prop overrides for icon, iconUrl, and color.
 * If not provided, falls back to useChatbotConfig() hook.
 */
export const ChatbotAvatar: FC<ChatbotAvatarProps> = ({
  className = '',
  icon: iconOverride,
  iconUrl: iconUrlOverride,
  color: colorOverride,
  colorOverTheme: colorOverThemeOverride,
}) => {
  const {
    config,
    color: configColor,
    colorOverTheme: configColorOverTheme,
  } = useChatbotConfig();

  // Use override props if provided, otherwise fall back to config
  const iconType = iconOverride ?? config?.icon;
  const iconUrl =
    iconUrlOverride !== undefined ? iconUrlOverride : config?.iconUrl;
  const color = colorOverride ?? configColor;
  const colorOverTheme = colorOverThemeOverride ?? configColorOverTheme;

  // Get the appropriate icon component based on configuration
  const getChatbotIcon = () => {
    switch (iconType) {
      case ChatbotIcon.Chatbot1:
        return Bot;
      case ChatbotIcon.Chatbot2:
        return MessageSquareMore;
      case ChatbotIcon.Chatbot3:
        return BotMessageSquare;
      case ChatbotIcon.Chatbot4:
        return MessageCircleMore;
      case ChatbotIcon.UserDefined:
        if (iconUrl) {
          return null; // Will render custom image instead
        }
        return Bot; // Fallback to Bot if no URL
      default:
        return Bot;
    }
  };

  const IconComponent = getChatbotIcon();

  // Determine avatar background: if colorOverTheme is white, use bg-muted; otherwise use colorOverTheme
  const isWhite = isWhiteColor(colorOverTheme);
  const avatarBgClass = isWhite ? 'bg-muted' : '';
  const avatarBgStyle =
    isWhite || !colorOverTheme ? {} : { backgroundColor: colorOverTheme };

  return (
    <div className={`flex-shrink-0 ${className}`}>
      <div
        className={`rounded-full p-2 ${avatarBgClass}`}
        style={avatarBgStyle}
      >
        {IconComponent ? (
          <IconComponent style={{ color }} className='w-6 h-6' />
        ) : (
          // Custom icon URL
          <img
            src={iconUrl ?? undefined}
            alt='Chatbot Avatar'
            className='w-6 h-6'
            style={{ color }}
          />
        )}
      </div>
    </div>
  );
};
